export interface OEmbedResponse {
  title: string;
  author_name: string;
  author_url: string;
  type: string;
  height: number;
  width: number;
  version: string;
  provider_name: string;
  provider_url: string;
  thumbnail_height: number;
  thumbnail_width: number;
  thumbnail_url: string;
  html: string;
}

abstract class VideoEmbedProvider {
  public abstract readonly name: string;

  public abstract readonly endpoint: string;

  public abstract readonly pattern: RegExp;

  public readonly proxy: boolean = false;

  public supports(resource: string) {
    return this.pattern.test(resource);
  }

  public getId(resource: string): string {
    if (!this.supports(resource)) {
      throw new Error('Resource not supported');
    }

    const result = this.pattern.exec(resource);

    if (!result?.[1]) {
      throw new Error('Invalid video');
    }

    return result[1];
  }

  public fetch(resource: string) {
    return $fetch<OEmbedResponse>(this.endpoint, {
      params: {
        format: 'json',
        url: resource,
      },
    });
  }

  public getEmbedUrl(resource: string): string {
    return resource;
  }
}

export class YoutubeProvider extends VideoEmbedProvider {
  public override name = 'youtube';

  public override endpoint = 'https://www.youtube.com/oembed';

  public override pattern = /^.*youtu(?:.*\/v\/|.*v=|\.be\/)([A-Za-z0-9_-]{11})/;

  public override getEmbedUrl(resource: string): string {
    return `https://www.youtube.com/embed/${this.getId(resource)}`;
  }
}

export class VimeoProvider extends VideoEmbedProvider {
  public override name = 'vimeo';

  public override endpoint = 'https://vimeo.com/api/oembed.json';

  public override pattern = /^.*(?:vimeo.com)\/(?:channels\/|channels\/\w+\/|groups\/[^/]*\/videos\/|album\/\d+\/video\/|video\/|)(\d+)(?:$|\/|\?)/;

  public override getEmbedUrl(resource: string): string {
    return `https://player.vimeo.com/video/${this.getId(resource)}`;
  }
}

export class RumbleProvider extends VideoEmbedProvider {
  public override name = 'rumble';

  public override endpoint = 'https://rumble.com/api/Media/oembed.json';

  public override pattern = /^.*(?:rumble.com\/embed\/)([a-z0-9]+){1}(?:$|\/|\?)/;

  public override proxy = true;

  public override getEmbedUrl(resource: string): string {
    return `https://rumble.com/embed/${this.getId(resource)}`;
  }
}

export const providers: VideoEmbedProvider[] = [
  new YoutubeProvider(),
  new VimeoProvider(),
  new RumbleProvider(),
];
